import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  Account,
  HoldingAnalysis,
  PortfolioSummary,
  SecurityInfo,
} from '../../types';
import * as portfolioThunks from '../thunks/portfolioThunks';

export interface PortfolioState {
  accounts: Account[];
  holdings: HoldingAnalysis[];
  portfolioSummary: PortfolioSummary | null;
  selectedAccountId: string | null;
  isLoading: {
    accounts: boolean;
    holdings: boolean;
    summary: boolean;
    creating: boolean;
    updating: boolean;
    deleting: boolean;
    validating: boolean;
  };
  error: string | null;
  tickerValidation: {
    ticker: string;
    isValid: boolean;
    security?: SecurityInfo;
    error?: string;
  } | null;
}

const initialState: PortfolioState = {
  accounts: [],
  holdings: [],
  portfolioSummary: null,
  selectedAccountId: null,
  isLoading: {
    accounts: false,
    holdings: false,
    summary: false,
    creating: false,
    updating: false,
    deleting: false,
    validating: false,
  },
  error: null,
  tickerValidation: null,
};

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    setSelectedAccount: (state, action: PayloadAction<string | null>) => {
      state.selectedAccountId = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    clearTickerValidation: state => {
      state.tickerValidation = null;
    },
    resetPortfolio: () => {
      return initialState;
    },
    // Additional reducers for mobile compatibility
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading.accounts = action.payload;
      state.isLoading.holdings = action.payload;
      state.isLoading.summary = action.payload;
      state.isLoading.creating = action.payload;
      state.isLoading.updating = action.payload;
      state.isLoading.deleting = action.payload;
      state.isLoading.validating = action.payload;
    },
    setAccounts: (state, action: PayloadAction<Account[]>) => {
      state.accounts = action.payload;
      state.error = null;
      // Set first account as selected if none selected
      if (!state.selectedAccountId && action.payload.length > 0) {
        state.selectedAccountId = action.payload[0].id;
      }
    },
    setHoldings: (state, action: PayloadAction<HoldingAnalysis[]>) => {
      state.holdings = action.payload;
      state.error = null;
    },
    addAccount: (state, action: PayloadAction<Account>) => {
      state.accounts.push(action.payload);
      // Set as selected if it's the first account
      if (state.accounts.length === 1) {
        state.selectedAccountId = action.payload.id;
      }
    },
    addHolding: (state, action: PayloadAction<HoldingAnalysis>) => {
      state.holdings.push(action.payload);
    },
    updateHoldingState: (state, action: PayloadAction<HoldingAnalysis>) => {
      const index = state.holdings.findIndex(h => h.id === action.payload.id);
      if (index !== -1) {
        state.holdings[index] = action.payload;
      }
    },
    removeHolding: (state, action: PayloadAction<string>) => {
      state.holdings = state.holdings.filter(h => h.id !== action.payload);
    },
    clearPortfolio: state => {
      state.accounts = [];
      state.holdings = [];
      state.portfolioSummary = null;
      state.selectedAccountId = null;
      state.error = null;
      state.tickerValidation = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      // Reset all loading states
      state.isLoading.accounts = false;
      state.isLoading.holdings = false;
      state.isLoading.summary = false;
      state.isLoading.creating = false;
      state.isLoading.updating = false;
      state.isLoading.deleting = false;
      state.isLoading.validating = false;
    },
  },
  extraReducers: builder => {
    // Fetch accounts
    builder
      .addCase(portfolioThunks.fetchAccounts.pending, state => {
        state.isLoading.accounts = true;
        state.error = null;
      })
      .addCase(portfolioThunks.fetchAccounts.fulfilled, (state, action) => {
        state.isLoading.accounts = false;
        state.accounts = action.payload;
        // Set first account as selected if none selected
        if (!state.selectedAccountId && action.payload.length > 0) {
          state.selectedAccountId = action.payload[0].id;
        }
      })
      .addCase(portfolioThunks.fetchAccounts.rejected, (state, action) => {
        state.isLoading.accounts = false;
        state.error = action.payload?.message || 'Failed to fetch accounts';
      })

      // Create account
      .addCase(portfolioThunks.createAccount.pending, state => {
        state.isLoading.creating = true;
        state.error = null;
      })
      .addCase(portfolioThunks.createAccount.fulfilled, (state, action) => {
        state.isLoading.creating = false;
        state.accounts.push(action.payload);
        // Set as selected if it's the first account
        if (state.accounts.length === 1) {
          state.selectedAccountId = action.payload.id;
        }
      })
      .addCase(portfolioThunks.createAccount.rejected, (state, action) => {
        state.isLoading.creating = false;
        state.error = action.payload?.message || 'Failed to create account';
      })

      // Fetch holdings
      .addCase(portfolioThunks.fetchHoldings.pending, state => {
        state.isLoading.holdings = true;
        state.error = null;
      })
      .addCase(portfolioThunks.fetchHoldings.fulfilled, (state, action) => {
        state.isLoading.holdings = false;
        state.holdings = action.payload;
      })
      .addCase(portfolioThunks.fetchHoldings.rejected, (state, action) => {
        state.isLoading.holdings = false;
        state.error = action.payload?.message || 'Failed to fetch holdings';
      })

      // Create holding
      .addCase(portfolioThunks.createHolding.pending, state => {
        state.isLoading.creating = true;
        state.error = null;
      })
      .addCase(portfolioThunks.createHolding.fulfilled, state => {
        state.isLoading.creating = false;
        // Note: We'll need to refetch holdings to get the analysis
      })
      .addCase(portfolioThunks.createHolding.rejected, (state, action) => {
        state.isLoading.creating = false;
        state.error = action.payload?.message || 'Failed to create holding';
      })

      // Update holding
      .addCase(portfolioThunks.updateHolding.pending, state => {
        state.isLoading.updating = true;
        state.error = null;
      })
      .addCase(portfolioThunks.updateHolding.fulfilled, state => {
        state.isLoading.updating = false;
        // Note: We'll need to refetch holdings to get updated analysis
      })
      .addCase(portfolioThunks.updateHolding.rejected, (state, action) => {
        state.isLoading.updating = false;
        state.error = action.payload?.message || 'Failed to update holding';
      })

      // Delete holding
      .addCase(portfolioThunks.deleteHolding.pending, state => {
        state.isLoading.deleting = true;
        state.error = null;
      })
      .addCase(portfolioThunks.deleteHolding.fulfilled, (state, action) => {
        state.isLoading.deleting = false;
        state.holdings = state.holdings.filter(h => h.id !== action.payload);
      })
      .addCase(portfolioThunks.deleteHolding.rejected, (state, action) => {
        state.isLoading.deleting = false;
        state.error = action.payload?.message || 'Failed to delete holding';
      })

      // Fetch portfolio summary
      .addCase(portfolioThunks.fetchPortfolioSummary.pending, state => {
        state.isLoading.summary = true;
        state.error = null;
      })
      .addCase(
        portfolioThunks.fetchPortfolioSummary.fulfilled,
        (state, action) => {
          state.isLoading.summary = false;
          state.portfolioSummary = action.payload;
        }
      )
      .addCase(
        portfolioThunks.fetchPortfolioSummary.rejected,
        (state, action) => {
          state.isLoading.summary = false;
          state.error =
            action.payload?.message || 'Failed to fetch portfolio summary';
        }
      )

      // Validate ticker
      .addCase(portfolioThunks.validateTicker.pending, state => {
        state.isLoading.validating = true;
        state.tickerValidation = null;
      })
      .addCase(portfolioThunks.validateTicker.fulfilled, (state, action) => {
        state.isLoading.validating = false;
        state.tickerValidation = action.payload;
      })
      .addCase(portfolioThunks.validateTicker.rejected, (state, action) => {
        state.isLoading.validating = false;
        state.error = action.payload?.message || 'Failed to validate ticker';
      });
  },
});

export const {
  setSelectedAccount,
  clearError,
  clearTickerValidation,
  resetPortfolio,
  setLoading,
  setAccounts,
  setHoldings,
  addAccount,
  addHolding,
  updateHoldingState,
  removeHolding,
  clearPortfolio,
  setError,
} = portfolioSlice.actions;
export default portfolioSlice.reducer;
